import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { captureAPI } from '../services/api';
import PageStrip from '../components/DocumentStudio/PageStrip';
import usePageRenderer from '../components/DocumentStudio/hooks/usePageRenderer';
import {
  DEFAULT_RAIL_WIDTH,
  MAX_RAIL_WIDTH,
  MIN_RAIL_WIDTH,
  boundRailWidth,
  loadRailWidth,
  renderWidthFor,
  saveRailWidth,
} from '../utils/splitRailPrefs';
import '../components/DocumentStudio/DocumentStudio.css';
import './SplitReviewPage.css';

/**
 * Verify an already-split container (slices 3b + 3d), and review a batch HELD
 * for boundary review before its documents exist (slice 3c-2).
 *
 * The rail is editable in BOTH modes — each gap toggles a cut, pages never
 * move. What differs is only where the cuts start from and what confirming
 * does, and both fall out of `partition`, which the server already defines per
 * mode:
 *
 *   HELD  — partition is the splitter's PROPOSAL; the documents do not exist.
 *           Unchanged → 'agreed', any cut moved → 'corrected'. Either answers
 *           202 and builds the documents in the background; this page polls
 *           until they exist.
 *   SPLIT — partition is the CHILDREN'S own pages; the documents already exist.
 *           Unchanged → 'agreed' or 'flagged'. Any cut moved → 'corrected',
 *           which records where the reviewer says documents begin and CHANGES
 *           NOTHING ELSE: no document is created, deleted or re-cut. The
 *           container is then flagged as carrying documents that do not match
 *           its verified boundaries, and the flag stays until something fixes
 *           them.
 *
 * One screen: the container's pages with the cuts the splitter made, the
 * evidence behind each document it produced, and a verdict. The verdict is what
 * the separation learner trains on, so the clean batches matter as much as the
 * doubtful ones: nothing on this page is gated on confidence. It is also why
 * correcting matters here at all — an 'agreed' that could not have been a
 * 'corrected' is not a verification.
 *
 * DELIBERATELY ABSENT: Reject, Approve & Continue, Re-extract and Document
 * Studio. On a split container Reject PATCHes status with no current-status
 * guard (it would turn the container into 'rejected'), Re-extract 409s,
 * Approve is refused by publish, and Studio's /restructure builds a second
 * overlapping set of children.
 */

export const REFUSAL_MESSAGES = {
  not_split: 'This document is not a split container, so there is no split to verify.',
  no_children: 'This container has no child documents, so there is no split to verify.',
  partition_not_tiled:
    'The child documents do not cover this container’s pages exactly once, so they cannot be verified as a split.',
  proposal_not_found:
    'The splitter’s proposal for these child documents was not recorded, so there is nothing to verify them against.',
  proposal_ambiguous:
    'More than one recorded proposal matches these child documents. Refusing to guess which one to stamp.',
  already_confirmed: 'This split has already been verified.',
  // Held batches (slice 3c-2).
  not_held: 'This batch is no longer waiting for a split review.',
  children_exist: 'This batch already has documents, so its split can no longer be reviewed before they are created.',
  no_paused_split: 'Nothing is waiting on this batch\u2019s split, so a confirmed split would have nothing to continue.',
  paused_split_ambiguous: 'More than one workflow run is waiting on this batch\u2019s split. Refusing to guess which one to continue.',
  confirm_stranded:
    'This split was confirmed, but its documents were never created and nothing is creating them now.',
  // Corrections (3c-2 held, 3d post-hoc).
  ranges_not_tiled: 'The corrected documents must cover every page exactly once.',
  corrected_without_change:
    'These boundaries are the ones already recorded. Record this as agreed instead.',
};

/**
 * Refusals that reject the SUBMITTED ranges and stamp nothing. The row is
 * untouched, so the reviewer may fix the cuts and try again — and the rail must
 * therefore keep their edit rather than snapping back and making them redo it.
 * Every other refusal is terminal.
 */
const RETRYABLE_REFUSALS = new Set(['ranges_not_tiled', 'corrected_without_change']);

const VERDICT_LABELS = {
  agreed: 'Agreed — the cuts are right',
  flagged: 'Flagged — something is wrong',
  corrected: 'Corrected — boundaries moved by the reviewer',
};

/** Ranges from the cut points of an N-page batch. */
export function rangesFromCuts(cuts, pageCount) {
  const starts = [0, ...[...cuts].filter((c) => c > 0 && c < pageCount).sort((a, b) => a - b)];
  return starts.map((start, i) => ({ start, end: i + 1 < starts.length ? starts[i + 1] - 1 : pageCount - 1 }));
}

const samePartition = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((r, i) => r.start === b[i].start && r.end === b[i].end);

const SIGNAL_LABELS = {
  document_number: 'document number',
  closing_phrase: 'closing phrase',
  header_block: 'first-page header',
};
const signalText = (signals) =>
  Array.isArray(signals) && signals.length > 0 ? signals.map((s) => SIGNAL_LABELS[s] || s).join(' + ') : 'none';

const STARTED_BY_LABELS = {
  text: 'text signals',
  stack_start: 'start of the batch',
  vision: 'vision (no text signal)',
  unresolved_gap: 'unresolved gap — cut rather than merged',
};

/**
 * Where to scroll `scroller` so `target` comes into view with its top `offset`
 * px below the scroller's top — or null when it is already in view, so a click
 * never moves a panel the reviewer is already looking at the right part of.
 * Rects are getBoundingClientRect()s. A target taller than the view counts as
 * in view only when its top is at the offset line.
 */
export function scrollTopToReveal(scrollerRect, targetRect, scrollTop, offset = 0) {
  const viewTop = scrollerRect.top + offset;
  const room = Math.max(0, scrollerRect.bottom - viewTop);
  const inView = targetRect.top >= viewTop && targetRect.top + Math.min(targetRect.height, room) <= scrollerRect.bottom;
  if (inView) return null;
  return Math.max(0, Math.round(scrollTop + (targetRect.top - scrollerRect.top) - offset));
}

function revealWithin(scroller, target, offset = 0) {
  if (!scroller || !target) return;
  const top = scrollTopToReveal(scroller.getBoundingClientRect(), target.getBoundingClientRect(), scroller.scrollTop, offset);
  if (top === null) return;
  if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top, behavior: 'smooth' });
  else scroller.scrollTop = top;
}

const RAIL_KEY_STEP = 16;

const pagesText = (r) => (r.start === r.end ? `Page ${r.start + 1}` : `Pages ${r.start + 1}–${r.end + 1}`);

function RangeCard({ index, range, child, evidenceRange, moved, selected, onSelect }) {
  const ev = evidenceRange?.evidence ?? null;
  const confidence = evidenceRange?.confidence ?? null;
  return (
    <li
      className={`sr-range ${selected ? 'sr-range--selected' : ''}`}
      data-testid={`sr-range-${index}`}
      onClick={() => onSelect(range.start)}
    >
      <div className="sr-range-head">
        <span className="sr-range-title">Document {index + 1}</span>
        <span className="sr-range-pages">{pagesText(range)}</span>
        {confidence && <span className={`sr-confidence sr-confidence--${confidence}`}>{confidence}</span>}
      </div>
      {child && (
        <div className="sr-range-child">
          <Link to={`/documents/${child.id}`} onClick={(e) => e.stopPropagation()}>{child.filename || child.id}</Link>
          {child.status && <span className="sr-range-child-status">{child.status}</span>}
        </div>
      )}
      {moved && (
        <p className="sr-range-moved" data-testid={`sr-range-moved-${index}`}>
          A boundary of this document was moved in this review — the splitter proposed different pages.
        </p>
      )}
      {ev ? (
        <dl className="sr-evidence">
          <dt>Started by</dt>
          <dd>{STARTED_BY_LABELS[ev.startedBy] || ev.startedBy}</dd>
          <dt>Start signals</dt>
          <dd className="sr-signals">{signalText(ev.signals)}</dd>
          <dt>End signals</dt>
          <dd className="sr-end-signals">{ev.endSignals === null ? 'end of batch' : signalText(ev.endSignals)}</dd>
          {Array.isArray(ev.details) && ev.details.length > 0 && (
            <>
              <dt>Details</dt>
              <dd>
                <ul className="sr-details">
                  {ev.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </dd>
            </>
          )}
          {Array.isArray(ev.verification) && ev.verification.length > 0 && (
            <>
              <dt>Checks that failed</dt>
              <dd>
                <ul className="sr-verification">
                  {ev.verification.map((v, i) => <li key={i}>{v.detail || v.kind}</li>)}
                </ul>
              </dd>
            </>
          )}
          {Array.isArray(ev.unreadPages) && ev.unreadPages.length > 0 && (
            <>
              <dt>Unread pages</dt>
              <dd>{ev.unreadPages.map((p) => p + 1).join(', ')}</dd>
            </>
          )}
          {ev.visionExamined && (
            <>
              <dt>Vision</dt>
              <dd>pages in this document were shown to vision</dd>
            </>
          )}
        </dl>
      ) : moved ? null : (
        <p className="sr-no-evidence">No evidence was recorded for this document.</p>
      )}
    </li>
  );
}

/**
 * The recorded verdict. It names the PERSON: the verdict's whole purpose is to
 * assert that someone looked, and a uuid asserts nothing to a reader.
 */
function PriorVerdict({ proposal, reviewer }) {
  if (!proposal?.verification_outcome) return null;
  const at = proposal.confirmed_at ? new Date(proposal.confirmed_at).toLocaleString() : 'an unknown time';
  const who = reviewer?.display_name
    ? reviewer.display_name
    : proposal.confirmed_by
      ? 'a reviewer who is no longer a member of this workspace'
      : null;
  return (
    <div className={`sr-verdict-recorded sr-verdict-recorded--${proposal.verification_outcome}`} data-testid="sr-prior-verdict">
      <strong>{VERDICT_LABELS[proposal.verification_outcome] || proposal.verification_outcome}</strong>
      <span>
        Recorded {at}
        {who ? ` by ${who}` : ''}
      </span>
    </div>
  );
}

const POLL_MS = 2500;

const SplitReviewPage = () => {
  const { documentId } = useParams();
  const [data, setData] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [pending, setPending] = useState(null); // 'agreed' | 'flagged' awaiting confirmation
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState(null); // { reason, detail } from a POST
  const [saveError, setSaveError] = useState(null);
  // Held batch: the reviewer's cut points (page indices that start a document).
  const [cuts, setCuts] = useState(null);
  // Held batch: a verdict was accepted (202) and its documents are being built.
  const [building, setBuilding] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [review, file] = await Promise.all([
        captureAPI.getSplitReview(documentId),
        captureAPI.getDocumentFileUrl(documentId).catch(() => null),
      ]);
      setData(review);
      setFileUrl(file?.url ?? null);
    } catch (err) {
      setLoadError(err?.response?.data?.error || err?.message || 'Failed to load the split');
    }
  }, [documentId]);

  useEffect(() => { load(); }, [load]);

  const isHeld = data?.mode === 'held';
  const proposalId = data?.proposal?.id ?? null;

  /**
   * Seed the cuts from `partition` once per proposal, never on a poll.
   *
   * WHICH partition needs no prop and no mode branch: the server already sends
   * the proposal's ranges for a held batch and the children's own pages for a
   * split one, under the same key. Held seeds from what the splitter proposed;
   * split seeds from the documents that exist — which is what a reviewer is
   * being asked about there.
   */
  useEffect(() => {
    if (!Array.isArray(data?.partition)) return;
    setCuts(new Set(data.partition.map((r) => r.start).filter((s) => s > 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  // While documents are being built, poll until the batch is no longer held or
  // the run that builds them has failed.
  useEffect(() => {
    if (!building) return undefined;
    const done = data && (data.container?.status !== 'pending_boundary_review' || data.execution?.status === 'failed');
    if (done) { setBuilding(false); return undefined; }
    const t = setTimeout(() => { load(); }, POLL_MS);
    return () => clearTimeout(t);
  }, [building, data, load]);

  const { loading: pdfLoading, error: pdfError, pageCount, renderThumbnail } = usePageRenderer(fileUrl);

  /**
   * The rail's width (slice 3e). While a drag is in progress the rail element's
   * style is written directly, so the panel follows the pointer continuously and
   * React — and with it every thumbnail — is not involved. The width is
   * committed to state and storage ONCE, when the drag ends, and only a
   * committed width can change the render size. This is the Processing
   * Monitor's column-resize mechanism.
   */
  const [railWidth, setRailWidth] = useState(() => loadRailWidth());
  const railRef = useRef(null);
  const panelRef = useRef(null);
  const resizeCleanupRef = useRef(null);
  useEffect(() => () => { if (resizeCleanupRef.current) resizeCleanupRef.current(); }, []);

  const commitRailWidth = useCallback((px) => {
    setRailWidth(saveRailWidth(px));
  }, []);

  const startRailResize = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const rail = railRef.current;
    if (!rail) return;
    const startX = e.clientX;
    const startWidth = railWidth;
    let finalWidth = startWidth;
    let raf = null;

    const onMove = (ev) => {
      finalWidth = boundRailWidth(startWidth + (ev.clientX - startX));
      if (raf == null) {
        raf = requestAnimationFrame(() => {
          rail.style.width = `${finalWidth}px`;
          raf = null;
        });
      }
    };
    const cleanup = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (raf != null) cancelAnimationFrame(raf);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      resizeCleanupRef.current = null;
    };
    const onUp = () => {
      cleanup();
      rail.style.width = `${finalWidth}px`;
      commitRailWidth(finalWidth);
    };
    resizeCleanupRef.current = cleanup;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [railWidth, commitRailWidth]);

  const onDividerKeyDown = (e) => {
    const next = e.key === 'ArrowRight' ? railWidth + RAIL_KEY_STEP
      : e.key === 'ArrowLeft' ? railWidth - RAIL_KEY_STEP
        : e.key === 'Home' ? MIN_RAIL_WIDTH
          : e.key === 'End' ? MAX_RAIL_WIDTH
            : null;
    if (next === null) return;
    e.preventDefault();
    commitRailWidth(boundRailWidth(next));
  };

  // One of a few fixed sizes, never the width itself: most drags change nothing.
  const renderWidth = renderWidthFor(railWidth);
  const renderKey = String(renderWidth);

  // How long the rail takes to render every thumbnail at the current render
  // sizes (they render one after another, each shown as it lands). Logged and
  // exposed on the page; measured afresh whenever the sizes step.
  const [thumbnailsMs, setThumbnailsMs] = useState(null);
  const timing = useRef({ key: null, startedAt: null, firstMs: null, done: new Set() });
  const timedRenderThumbnail = useCallback(
    async (pageIndex, rotation, width) => {
      if (timing.current.key !== renderKey) {
        timing.current = { key: renderKey, startedAt: performance.now(), firstMs: null, done: new Set() };
        setThumbnailsMs(null);
      }
      const t = timing.current;
      const url = await renderThumbnail(pageIndex, rotation, width);
      if (t !== timing.current) return url;
      if (t.firstMs === null) t.firstMs = Math.round(performance.now() - t.startedAt);
      t.done.add(pageIndex);
      if (pageCount > 0 && t.done.size === pageCount && !t.reported) {
        t.reported = true;
        const ms = Math.round(performance.now() - t.startedAt);
        console.info(`[split-review] thumbnails pages=${pageCount} render=${renderKey} first=${t.firstMs}ms ms=${ms}`);
        setThumbnailsMs(ms);
      }
      return url;
    },
    [renderThumbnail, pageCount, renderKey],
  );

  const children = useMemo(
    () => [...(data?.children ?? [])].sort((a, b) => (a.start ?? Infinity) - (b.start ?? Infinity)),
    [data],
  );
  // The partition when it tiles; otherwise what the children say, so a reviewer
  // can still see where the gap or overlap is.
  // A refusal that only rejected the submitted ranges leaves the verdict open.
  const blocked = Boolean(refusal && !RETRYABLE_REFUSALS.has(refusal.reason));
  const editable = Boolean(data?.readiness?.ok && !blocked && !building && cuts);
  // Set once a post-hoc correction is recorded: the documents that exist are
  // NOT the boundaries a person verified, and nothing is going to change that
  // on its own.
  const documentsDisagree = Array.isArray(data?.proposal?.documents_ranges)
    ? data.proposal.documents_ranges
    : null;
  const ranges = useMemo(() => {
    if (editable && data?.container?.page_count) return rangesFromCuts(cuts, data.container.page_count);
    return data?.partition ?? children.filter((c) => c.start != null && c.end != null).map((c) => ({ start: c.start, end: c.end }));
  }, [editable, cuts, data, children]);
  const changed = editable && !samePartition(ranges, data?.partition);
  const proposedRanges = useMemo(() => data?.proposal?.proposed_ranges?.pageRanges ?? [], [data]);

  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => ({ originalIndex: i, rotation: 0, deleted: false, annotation: null })),
    [pageCount],
  );
  const splits = useMemo(() => new Set(ranges.map((r) => r.start).filter((s) => s > 0)), [ranges]);
  // The proposal's range with exactly these pages, if the splitter proposed it.
  const proposedFor = useCallback(
    (r) => proposedRanges.find((p) => p.start === r.start && p.end === r.end) ?? null,
    [proposedRanges],
  );
  const cutConfidence = useMemo(
    () =>
      ranges
        .map((r) => ({ beforePageIndex: r.start, confidence: proposedFor(r)?.confidence ?? null }))
        .filter((c) => c.beforePageIndex > 0 && typeof c.confidence === 'string'),
    [ranges, proposedFor],
  );

  const noop = useCallback(() => {}, []);
  const toggleCut = useCallback((beforePageIndex) => {
    setCuts((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(beforePageIndex)) next.delete(beforePageIndex); else next.add(beforePageIndex);
      return next;
    });
  }, []);
  /**
   * Scroll syncing. Each panel scrolls ONLY in direct response to a click in the
   * other one — never from an effect on the selection — so polling, a thumbnail
   * landing, or a cut being moved can never pull a panel away from where the
   * reviewer scrolled it. And a panel already showing its target is left alone.
   */
  const selectFromRail = useCallback((pageIndex) => {
    setSelectedPage(pageIndex);
    const panel = panelRef.current;
    const i = ranges.findIndex((r) => pageIndex >= r.start && pageIndex <= r.end);
    const card = i >= 0 ? panel?.querySelector(`[data-testid="sr-range-${i}"]`) : null;
    const verdict = panel?.querySelector('.sr-verdict');
    // The verdict bar is sticky over the top of the column (top: -16px).
    revealWithin(panel, card, Math.max(0, (verdict?.offsetHeight ?? 0) - 16) + 8);
  }, [ranges]);

  const selectFromCard = useCallback((startPage) => {
    setSelectedPage(startPage);
    const rail = railRef.current;
    revealWithin(rail, rail?.querySelector(`[data-doc-start="${startPage}"]`));
  }, []);

  const resetCuts = () => setCuts(new Set((data?.partition ?? []).map((r) => r.start).filter((s) => s > 0)));
  const resetLabel = isHeld ? 'Reset to proposal' : 'Reset to the documents';

  const retry = async () => {
    setSaving(true);
    setSaveError(null);
    setRefusal(null);
    try {
      const res = await captureAPI.retrySplitReentry(documentId);
      if (res?.ok) {
        setData((prev) => ({ ...prev, readiness: { ok: false, reason: 'already_confirmed', detail: null } }));
        setBuilding(true);
      } else {
        setRefusal({ reason: res?.error, detail: res?.details ?? null });
      }
    } catch (err) {
      setSaveError(err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to retry');
    } finally {
      setSaving(false);
    }
  };

  const record = async (outcome) => {
    setSaving(true);
    setSaveError(null);
    setRefusal(null);
    try {
      const res = await captureAPI.recordSplitVerdict(documentId, outcome, outcome === 'corrected' ? ranges : undefined);
      if (res?.ok) {
        setData((prev) => ({ ...prev, proposal: res.verdict, reviewer: res.reviewer ?? prev.reviewer, readiness: { ok: false, reason: 'already_confirmed', detail: null } }));
        if (res.reentry) setBuilding(true);
      } else {
        setRefusal({ reason: res?.error, detail: res?.details ?? null });
        if (res?.verdict) setData((prev) => ({ ...prev, proposal: res.verdict, reviewer: res.reviewer ?? prev.reviewer, readiness: { ok: false, reason: 'already_confirmed', detail: res.details } }));
      }
    } catch (err) {
      setSaveError(err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to record the verdict');
    } finally {
      setSaving(false);
      setPending(null);
    }
  };

  if (loadError) {
    return (
      <div className="sr-page">
        <div className="sr-banner sr-banner--error">{loadError}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="sr-page"><div className="sr-loading">Loading split…</div></div>;
  }

  const { container, readiness } = data;
  const selectedRangeIndex = ranges.findIndex((r) => selectedPage >= r.start && selectedPage <= r.end);
  const shownReason = refusal?.reason ?? (readiness.ok ? null : readiness.reason);
  const shownDetail = refusal ? refusal.detail : readiness.detail;
  const pageCountMismatch = pageCount > 0 && container.page_count != null && pageCount !== container.page_count;

  return (
    <div className="sr-page">
      <header className="sr-header">
        <Link to="/processing" className="sr-back">‹ Processing</Link>
        <div className="sr-title">
          <h1>{isHeld ? 'Review split' : 'Verify split'}</h1>
          <span className="sr-filename">{container.filename}</span>
        </div>
        <span className="sr-summary">
          {ranges.length} document{ranges.length === 1 ? '' : 's'} · {container.page_count ?? '?'} pages
          {thumbnailsMs !== null && <span className="sr-timing" data-testid="sr-thumbnails-ms"> · thumbnails {thumbnailsMs} ms</span>}
        </span>
      </header>

      {pageCountMismatch && (
        <div className="sr-banner sr-banner--warning">
          The stored PDF has {pageCount} pages but the container records {container.page_count}.
        </div>
      )}

      <div className="sr-body">
        <aside
          className="sr-rail ds-thumbnail-rail"
          aria-label="Pages and cuts"
          ref={railRef}
          style={{ width: `${railWidth}px` }}
          data-testid="sr-rail"
        >
          {pdfLoading && fileUrl && <div className="sr-loading">Loading pages…</div>}
          {(pdfError || (!fileUrl && !pdfLoading)) && <div className="sr-banner sr-banner--error">The PDF could not be loaded.</div>}
          {pageCount > 0 && (
            <PageStrip
              readOnly
              cutsEditable={editable}
              pages={pages}
              splits={splits}
              selectedPageIndex={selectedPage}
              aiSuggestions={cutConfidence}
              renderThumbnail={timedRenderThumbnail}
              groupByDocument
              renderWidth={renderWidth}
              onSelectPage={selectFromRail}
              onReorderPages={noop}
              onToggleSplit={editable ? toggleCut : noop}
            />
          )}
        </aside>

        <div
          className="sr-divider"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the page rail"
          aria-valuemin={MIN_RAIL_WIDTH}
          aria-valuemax={MAX_RAIL_WIDTH}
          aria-valuenow={railWidth}
          tabIndex={0}
          title="Drag to resize the pages. Double-click to reset."
          data-testid="sr-divider"
          onMouseDown={startRailResize}
          onDoubleClick={() => commitRailWidth(DEFAULT_RAIL_WIDTH)}
          onKeyDown={onDividerKeyDown}
        />

        <main className="sr-panel" ref={panelRef} data-testid="sr-panel">
          <section className="sr-verdict" aria-label="Verdict">
            {shownReason && (
              <div
                className={`sr-banner ${shownReason === 'already_confirmed' ? 'sr-banner--info' : 'sr-banner--warning'}`}
                data-testid="sr-refusal"
                data-reason={shownReason}
              >
                <strong>{REFUSAL_MESSAGES[shownReason] || shownReason}</strong>
                {shownDetail && <span className="sr-refusal-detail">{shownDetail}</span>}
              </div>
            )}
            <PriorVerdict proposal={data.proposal} reviewer={data.reviewer} />
            {documentsDisagree && (
              <div className="sr-banner sr-banner--warning" data-testid="sr-documents-disagree">
                <strong>
                  The documents that exist do not match the verified boundaries.
                </strong>
                <span className="sr-refusal-detail">
                  A reviewer recorded {data.proposal.confirmed_ranges?.length ?? 0} document
                  {(data.proposal.confirmed_ranges?.length ?? 0) === 1 ? '' : 's'} here, but
                  {' '}{documentsDisagree.length} {documentsDisagree.length === 1 ? 'was' : 'were'} already
                  created and were left as they are. Recording the correction did not re-cut them.
                </span>
              </div>
            )}
            {saveError && <div className="sr-banner sr-banner--error">{saveError}</div>}

            {isHeld && (building || (data.execution?.status === 'running' && readiness.reason !== 'confirm_stranded')) && data.container.status === 'pending_boundary_review' && (
              <div className="sr-banner sr-banner--progress" data-testid="sr-building">
                <strong>Creating {ranges.length} document{ranges.length === 1 ? '' : 's'} from these pages…</strong>
                <span className="sr-refusal-detail">This page updates when they exist.</span>
              </div>
            )}
            {isHeld && readiness.reason === 'confirm_stranded' && !building && !blocked && (
              <div className="sr-actions" data-testid="sr-stranded">
                <p className="sr-question">The recorded verdict stands; retrying creates the documents from it without asking again.</p>
                <button className="sr-btn sr-btn--agree" onClick={retry} disabled={saving}>
                  {saving ? 'Retrying…' : 'Retry creating the documents'}
                </button>
              </div>
            )}
            {isHeld && data.execution?.status === 'failed' && data.proposal?.verification_outcome && readiness.reason !== 'confirm_stranded' && (
              <div className="sr-banner sr-banner--error" data-testid="sr-build-failed">
                <strong>Creating the documents failed.</strong>
                <span className="sr-refusal-detail">The verdict is recorded. Retry the workflow run from the Processing page.</span>
              </div>
            )}

            {readiness.ok && !blocked && isHeld && (
              pending ? (
                <div className="sr-confirm">
                  <span>
                    Create <strong>{ranges.length}</strong> document{ranges.length === 1 ? '' : 's'} from these pages
                    {pending === 'corrected' ? ' with your corrected boundaries' : ' as proposed'}? This cannot be undone.
                  </span>
                  <button className="sr-btn sr-btn--primary" onClick={() => record(pending)} disabled={saving}>
                    {saving ? 'Recording…' : 'Confirm'}
                  </button>
                  <button className="sr-btn" onClick={() => setPending(null)} disabled={saving}>Cancel</button>
                </div>
              ) : (
                <div className="sr-actions">
                  <p className="sr-question">
                    {changed
                      ? 'You moved a boundary. Click a gap between pages to add or remove a cut.'
                      : 'Is this where each document begins? Click a gap between pages to add or remove a cut.'}
                  </p>
                  {changed ? (
                    <>
                      <button className="sr-btn sr-btn--agree" onClick={() => setPending('corrected')}>Confirm corrected split</button>
                      <button className="sr-btn" onClick={resetCuts}>{resetLabel}</button>
                    </>
                  ) : (
                    <button className="sr-btn sr-btn--agree" onClick={() => setPending('agreed')}>Confirm split</button>
                  )}
                </div>
              )
            )}

            {readiness.ok && !blocked && !isHeld && (
              pending ? (
                <div className="sr-confirm">
                  <span>
                    {pending === 'corrected' ? (
                      <>
                        Record these <strong>{ranges.length}</strong> boundaries as the right ones?
                        The {children.length} document{children.length === 1 ? '' : 's'} that already exist
                        {' '}<strong>will not change</strong> — this records what is true, and they stay as they are
                        until something fixes them. A verdict cannot be changed afterwards.
                      </>
                    ) : (
                      <>Record <strong>{pending}</strong> for all {ranges.length} documents? A verdict cannot be changed afterwards.</>
                    )}
                  </span>
                  <button className="sr-btn sr-btn--primary" onClick={() => record(pending)} disabled={saving}>
                    {saving ? 'Recording…' : 'Confirm'}
                  </button>
                  <button className="sr-btn" onClick={() => setPending(null)} disabled={saving}>Cancel</button>
                </div>
              ) : (
                <div className="sr-actions">
                  <p className="sr-question">
                    {changed
                      ? 'You moved a boundary. Recording this does not re-cut the documents — it records where they should have been.'
                      : 'Are these the right places to start a new document? Click a gap between pages to move a cut.'}
                  </p>
                  {changed ? (
                    <>
                      <button className="sr-btn sr-btn--agree" onClick={() => setPending('corrected')}>Record corrected boundaries</button>
                      <button className="sr-btn" onClick={resetCuts}>{resetLabel}</button>
                    </>
                  ) : (
                    <>
                      <button className="sr-btn sr-btn--agree" onClick={() => setPending('agreed')}>Agree</button>
                      <button className="sr-btn sr-btn--flag" onClick={() => setPending('flagged')}>Flag</button>
                    </>
                  )}
                </div>
              )
            )}
          </section>

          <ol className="sr-ranges" aria-label="Documents the splitter produced">
            {ranges.map((r, i) => (
              <RangeCard
                key={`${r.start}-${r.end}`}
                index={i}
                range={r}
                child={children.find((c) => c.start === r.start && c.end === r.end)}
                evidenceRange={data.proposal ? proposedFor(r) : null}
                moved={Boolean(data.proposal && !proposedFor(r))}
                selected={i === selectedRangeIndex}
                onSelect={selectFromCard}
              />
            ))}
          </ol>
        </main>
      </div>
    </div>
  );
};

export default SplitReviewPage;
